import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { SplitWords, FadeSlideIn } from './AnimatedText';
import { TEST_IDS } from '@/constants/testIds';

const TESTIMONIALS = [
  {
    quote: "Sarah dimensionally understood our team dynamics in a way no one else could. The lights went on — we found clarity, inspiration, and a breakthrough we didn't think was possible.",
    name: 'Seth Gaffney', title: 'Founder, Preacher',
  },
  {
    quote: "She identified and articulated the thing I couldn't see. A drop of light in exactly the right place. Matt and I now have a new verbal and visual vocabulary for our leadership.",
    name: 'Matt Gibberd', title: 'Founder, The Modern House',
  },
  {
    quote: "Working with Sarah turned burnout into a new creative vision. She reminded us who we are — and gave us the energy and tools to build on that foundation.",
    name: 'Faye Toogood', title: 'Founder, Toogood',
  },
  {
    quote: "Sarah gave us the crystalline truths we needed to navigate post-acquisition complexity. She goes deeper than any advisor I've worked with.",
    name: 'Georgie Greville', title: 'Co-Founder & Creative Director, Milk Makeup',
  },
  {
    quote: "Sarah helped me get through the toughest year of my professional life. I emerged renewed and energized, with a completely new understanding of my leadership.",
    name: 'Ian Pierpont', title: 'Founder & CEO, Further & Further Strategies',
  },
  {
    quote: "She brought tangible clarity to our leadership intentionality. The transformation was real — not abstract. I'd recommend Sarah to any leader ready for deep work.",
    name: 'Jinal Shah', title: 'GM and CMO, Zip US',
  },
];

export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  const next = useCallback(() => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % TESTIMONIALS.length);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  }, []);

  // Auto-rotate every 6 seconds
  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slideVariants = {
    enter: (dir) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (dir) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
  };

  return (
    <section
      id="testimonials"
      data-testid={TEST_IDS.testimonialsSection}
      className="bg-[#F7F5F2] py-24 md:py-32 px-6 md:px-12 relative"
    >
      <div className="max-w-7xl mx-auto">
        <FadeSlideIn className="mb-16 md:mb-20">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] text-[#C9A96E] font-sans font-semibold mb-6">
            <motion.span
              initial={{ width: 0 }}
              whileInView={{ width: 30 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="inline-block h-px bg-[#C9A96E] mr-3 align-middle"
            />
            Client Voices
          </p>
          <h2
            data-testid={TEST_IDS.testimonialsHeadline}
            className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight text-[#1B2A4A] max-w-2xl"
          >
            <SplitWords>What Leaders Say</SplitWords>
          </h2>
        </FadeSlideIn>

        {/* Featured Testimonial Slider */}
        <div className="mb-16">
          <div className="relative max-w-4xl min-h-[220px]">
            <Quote className="w-20 h-20 text-[#C9A96E]/10 absolute -top-6 -left-4" />

            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={current}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="pl-6 md:pl-10 border-l-2 border-[#C9A96E]"
              >
                <p className="font-serif text-xl sm:text-2xl md:text-3xl text-[#1B2A4A] italic leading-relaxed mb-8">
                  &ldquo;{TESTIMONIALS[current].quote}&rdquo;
                </p>
                <div className="flex items-center gap-4">
                  <motion.div
                    className="w-1 h-8 bg-[#C9A96E]/30"
                    initial={{ height: 0 }}
                    animate={{ height: 32 }}
                    transition={{ duration: 0.4, delay: 0.2 }}
                  />
                  <div>
                    <p className="font-sans font-medium text-[#1B2A4A] text-base">
                      {TESTIMONIALS[current].name}
                    </p>
                    <p className="text-xs uppercase tracking-[0.15em] text-[#2D2D2D]/40 font-sans mt-0.5">
                      {TESTIMONIALS[current].title}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Controls */}
            <div className="flex items-center gap-4 mt-10 pl-6 md:pl-10">
              <motion.button
                onClick={prev}
                data-testid="testimonial-prev-btn"
                className="w-10 h-10 border border-[#1B2A4A]/20 flex items-center justify-center hover:bg-[#1B2A4A] hover:text-[#F7F5F2] transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronLeft className="w-4 h-4" />
              </motion.button>

              {/* Dot indicators */}
              <div className="flex items-center gap-2">
                {TESTIMONIALS.map((_, i) => (
                  <motion.button
                    key={i}
                    onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); }}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === current ? 'bg-[#C9A96E] w-6' : 'bg-[#1B2A4A]/15 w-1.5 hover:bg-[#C9A96E]/50'
                    }`}
                    whileHover={{ scale: 1.3 }}
                    data-testid={`testimonial-dot-${i}`}
                  />
                ))}
              </div>

              <motion.button
                onClick={next}
                data-testid="testimonial-next-btn"
                className="w-10 h-10 border border-[#1B2A4A]/20 flex items-center justify-center hover:bg-[#1B2A4A] hover:text-[#F7F5F2] transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          </div>
        </div>

        {/* Testimonial Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              className={`p-8 border-l-2 border-[#C9A96E] cursor-pointer transition-all duration-400 ${
                i === current ? 'bg-[#1B2A4A] glow-gold' : 'bg-[#E8E0D5] hover:bg-[#E8E0D5]/70'
              }`}
              onClick={() => { setDirection(i > current ? 1 : -1); setCurrent(i); }}
              data-testid={`testimonial-card-${i}`}
            >
              <p className={`font-serif text-base italic leading-relaxed mb-6 line-clamp-4 ${
                i === current ? 'text-[#F7F5F2]' : 'text-[#1B2A4A]'
              }`}>
                &ldquo;{t.quote}&rdquo;
              </p>
              <div>
                <p className={`font-sans font-medium text-sm ${i === current ? 'text-[#F7F5F2]' : 'text-[#1B2A4A]'}`}>
                  {t.name}
                </p>
                <p className={`text-xs font-sans mt-0.5 ${i === current ? 'text-[#C9A96E]' : 'text-[#2D2D2D]/40'}`}>
                  {t.title}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
